import {
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  BoundingBox,
  BracketsSquare,
  ChatText,
  FileText,
  HardDrives,
  Plus,
} from '@phosphor-icons/react';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import React, { useState } from 'react';
import { useDarkMode } from '../../../hooks/useDarkMode';
import { CustomColorPalette } from '../../../theme';
import { CreateItemModal } from './CreateItemModal';
import { ExtendedSidebar } from './ExtendedSidebar';
import { CreateItemType } from './types';

const MenuItemButton = styled(ListItemButton)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    display: 'flex',
    padding: '4px 8px',
    alignItems: 'center',
    gap: '8px',
    alignSelf: 'stretch',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[200],
    },
  };
});

const MenuItemIconStyled = styled(ListItemIcon)({
  minWidth: 'auto',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const menuItemTextSx = (isDarkMode: boolean, palette: CustomColorPalette) => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical' as const,
  WebkitLineClamp: 1,
  flex: '1 0 0',
  overflow: 'hidden',
  color: isDarkMode ? palette.gray[300] : palette.gray[600],
  fontFeatureSettings: "'liga' off, 'clig' off",
  textOverflow: 'ellipsis',
  fontFamily: 'Roboto',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '150%',
  letterSpacing: '0.15px',
  margin: 0,
});

interface MenuItemConfig {
  type: CreateItemType;
  icon: PhosphorIcon;
  label: string;
}

const menuItems: MenuItemConfig[] = [
  { type: 'agent', icon: BoundingBox, label: 'Add Agent' },
  { type: 'interface', icon: FileText, label: 'Add Interface' },
  { type: 'fluxbot', icon: ChatText, label: 'Add Fluxbot' },
  { type: 'object', icon: BracketsSquare, label: 'Add Object' },
  { type: 'vectorMemory', icon: HardDrives, label: 'Add Vector Memory' },
];

interface PlusMenuProps {
  children?: React.ReactNode;
  allowedTypes?: CreateItemType[];
}

export const PlusMenu: React.FC<PlusMenuProps> = ({ allowedTypes }) => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const [plusMenuAnchor, setPlusMenuAnchor] =
    useState<HTMLButtonElement | null>(null);
  const [createModalType, setCreateModalType] = useState<CreateItemType | null>(
    null,
  );

  const handlePlusClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (event) {
      setPlusMenuAnchor(event.currentTarget);
    }
  };

  const handlePlusMenuClose = () => {
    setPlusMenuAnchor(null);
  };

  const handleCreateClick = (type: CreateItemType) => {
    setCreateModalType(type);
    handlePlusMenuClose();
  };

  const handleModalClose = () => {
    setCreateModalType(null);
  };

  const textSx = menuItemTextSx(isDarkMode, palette);
  const filteredMenuItems = allowedTypes
    ? menuItems.filter(item => allowedTypes.includes(item.type))
    : menuItems;

  return (
    <>
      <ExtendedSidebar.ActionButton icon={Plus} onClick={handlePlusClick} />

      <Popover
        open={Boolean(plusMenuAnchor)}
        anchorEl={plusMenuAnchor}
        onClose={handlePlusMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              display: 'flex',
              width: '229px',
              padding: '8px',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: '16px',
              borderRadius: '8px',
              border: `1px solid ${
                isDarkMode ? palette.gray[700] : palette.gray[300]
              }`,
              backgroundColor: isDarkMode
                ? palette.gray[900]
                : palette.gray.A100,
              marginTop: '8px',
              boxShadow: 'none',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%',
          }}
        >
          {filteredMenuItems.map(item => {
            const ItemIcon = item.icon;
            return (
              <MenuItemButton
                key={item.type}
                onClick={() => handleCreateClick(item.type)}
              >
                <MenuItemIconStyled>
                  <ItemIcon
                    size={20}
                    color={isDarkMode ? palette.gray[400] : palette.gray[500]}
                  />
                </MenuItemIconStyled>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ sx: textSx }}
                />
              </MenuItemButton>
            );
          })}
        </Box>
      </Popover>

      {createModalType && (
        <CreateItemModal
          show={true}
          onHide={handleModalClose}
          type={createModalType}
        />
      )}
    </>
  );
};
